import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import React from "react"
import Grid from "@mui/material/Grid"
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined"
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined"
import SportsScoreOutlinedIcon from "@mui/icons-material/SportsScoreOutlined"
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined"
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined"
import parse from "html-react-parser"
import { connectHandler } from "../../../../utils/connectHandler"
import { useAppDispatch, useAppSelector } from "../../../../store"
import { useTournamentDialogs } from "../../hooks/useTournamentDialogs"
import { TournamentActionButton } from "../TournamentActionButton"
import { PATHS } from "../../../../constants/paths"

const Panel = ({ title, children, sx }) => (
  <Box
    sx={{
      height: "100%",
      p: {
        xs: 2,
        md: 2.2,
      },
      borderRadius: 1,
      border: "1px solid rgba(196,150,84,0.2)",
      background: "linear-gradient(180deg, #000 60%, #121212 100%)",
      ...sx,
    }}
  >
    {title && (
      <Typography variant="h4" mb={2}>
        {title}
      </Typography>
    )}

    {children}
  </Box>
)

export default function Overview({ tournament }) {
  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state) => state.user)

  const { openJoin, openGame } = useTournamentDialogs({
    tournament,
    user,
    successBackPath: `${PATHS.TOURNAMENT_DETAILS.PATH}${tournament.id}`,
  })

  return (
    <Grid container spacing={2.4}>
      <Grid item xs={12} md={4}>
        <Panel title="About This Tournament">
          <Typography
            sx={{
              fontSize: "1.1rem",
              color: "#dedede",
              whiteSpace: "pre-line",
            }}
          >
            {parse(tournament.description || "")}
          </Typography>
        </Panel>
      </Grid>

      <Grid item xs={12} md={4}>
        <Panel title="Tournament info">
          {[
            {
              icon: <CalendarTodayOutlinedIcon />,
              label: "Starts",
              value: tournament.start_at,
            },
            {
              icon: <CalendarTodayOutlinedIcon />,
              label: "Ends",
              value: tournament.end_at,
            },
            {
              icon: <LocalFireDepartmentOutlinedIcon />,
              label: "Attempts",
              value: tournament.max_attempts,
            },
            {
              icon: <SportsScoreOutlinedIcon />,
              label: "Scoring",
              value: "Points for killing zombies",
            },
            {
              icon: <TimerOutlinedIcon />,
              label: "Fee",
              value: tournament.platform_fee,
            },
            {
              icon: <PublicOutlinedIcon />,
              label: "Region",
              value: "Global",
            },
          ].map((item) => (
            <Box
              key={item.label}
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(110px, 0.9fr) 1fr",
                gap: 1.5,
                alignItems: "start",
                py: 1,
                borderBottom: "1px solid rgba(198,146,76,0.1)",
                "&:last-of-type": {
                  borderBottom: "none",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.8,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    color: "#827b72",
                    "& svg": {
                      fontSize: 17,
                    },
                  }}
                >
                  {item.icon}
                </Box>

                <Typography
                  sx={{
                    color: "#dedede",
                    fontSize: 16,
                  }}
                >
                  {item.label}
                </Typography>
              </Box>

              <Typography
                sx={{
                  color: "#dedede",
                  fontSize: 16,
                  fontWeight: 700,
                }}
              >
                {item.value}
              </Typography>
            </Box>
          ))}
        </Panel>
      </Grid>

      <Grid item xs={12} md={4}>
        <Panel title="Your Tournament Stats">
          <Box
            sx={{
              display: "grid",
              gap: 1.2,
            }}
          >
            {[
              ["Best Score", tournament.player?.best_score ?? "—"],
              [
                "Current Rank",
                `${tournament.player?.place ?? "—"} / ${tournament.player?.players_amount ?? "—"}`,
              ],
              ["Current Reward", `${tournament.player?.reward ?? "—"}`],
              [
                "Attempts",
                `${tournament.player?.attempts ?? "—"} / ${tournament.max_attempts ?? "—"}`,
              ],
            ].map(([label, value], index) => (
              <Box
                key={label}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                  pb: 1.1,
                  borderBottom: "1px solid rgba(198,146,76,0.1)",
                }}
              >
                <Typography
                  sx={{
                    color: "#dedede",
                    fontSize: 16,
                  }}
                >
                  {label}
                </Typography>

                <Typography
                  sx={{
                    color: index === 1 || index === 3 ? "#35c66b" : "#d7d0c3",
                    fontSize: 16,
                    fontWeight: 900,
                  }}
                >
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>

          {!tournament?.is_finished && (
            <TournamentActionButton
              tournament={tournament}
              user={user}
              onConnect={() => connectHandler(dispatch)}
              onJoin={openJoin}
              onPlay={openGame}
            />
          )}

          {tournament?.is_banned && (
            <Box>
              <Typography
                sx={{
                  color: "#E60F04",
                  fontSize: 16,
                  fontWeight: 900,
                  textAlign: "center",
                  mt: 1,
                }}
              >
                You are banned
              </Typography>
            </Box>
          )}
        </Panel>
      </Grid>
    </Grid>
  )
}
